import type {
  TeamMember,
  TeamProps,
  TeamSocial,
  TeamStyle,
} from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { ColorField, SelectField, TextField } from "./fields.js";
import { MediaField } from "./MediaField.js";
import type { WidgetInspectorProps } from "./registry.js";

const COLS = ["2", "3", "4"] as const;
const STYLE = ["card", "minimal", "circle"] as const;

function newId(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 8)}`;
}

export function TeamInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as TeamProps;
  const members = p.members ?? [];

  const setMembers = (next: TeamMember[]) => update(widget.id, { members: next });

  const addMember = () =>
    setMembers([
      ...members,
      {
        id: newId("m"),
        name: "New member",
        role: "Role",
        photoUrl: "https://placehold.co/320x320/e5e7eb/6b7280?text=?",
      },
    ]);
  const updateMember = (id: string, patch: Partial<TeamMember>) =>
    setMembers(members.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const removeMember = (id: string) => setMembers(members.filter((m) => m.id !== id));
  const moveMember = (id: string, dir: "up" | "down") => {
    const idx = members.findIndex((m) => m.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= members.length) return;
    const next = members.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setMembers(next);
  };

  return (
    <>
      <SelectField
        label="Columns"
        value={String(p.columns ?? 3) as (typeof COLS)[number]}
        options={COLS}
        onChange={(v) =>
          update(widget.id, {
            columns: Number.parseInt(v, 10) as TeamProps["columns"],
          })
        }
      />
      <SelectField
        label="Style"
        value={p.style ?? "card"}
        options={STYLE}
        onChange={(v) => update(widget.id, { style: v as TeamStyle })}
      />
      <ColorField
        label="Card background"
        value={p.cardBackground ?? "#ffffff"}
        onChange={(v) => update(widget.id, { cardBackground: v })}
      />
      <ColorField
        label="Text"
        value={p.textColor ?? "#0f172a"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />
      <ColorField
        label="Accent (role + socials)"
        value={p.accent ?? "#111827"}
        onChange={(v) => update(widget.id, { accent: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Members ({members.length})
      </div>

      {members.map((m, i) => {
        const socials = m.socials ?? [];
        return (
          <div
            key={m.id}
            style={{
              padding: "8px 16px 10px",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 4,
                justifyContent: "flex-end",
                marginBottom: 4,
              }}
            >
              <button
                type="button"
                className="emke-btn"
                onClick={() => moveMember(m.id, "up")}
                disabled={i === 0}
                style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
              >
                ↑
              </button>
              <button
                type="button"
                className="emke-btn"
                onClick={() => moveMember(m.id, "down")}
                disabled={i === members.length - 1}
                style={{
                  padding: "2px 6px",
                  opacity: i === members.length - 1 ? 0.4 : 1,
                }}
              >
                ↓
              </button>
              <button
                type="button"
                className="emke-btn is-danger"
                onClick={() => removeMember(m.id)}
                style={{ padding: "2px 6px" }}
              >
                ✕
              </button>
            </div>
            <TextField
              label={`Name ${i + 1}`}
              value={m.name}
              onChange={(v) => updateMember(m.id, { name: v })}
            />
            <TextField
              label="Role"
              value={m.role}
              onChange={(v) => updateMember(m.id, { role: v })}
            />
            <MediaField
              label="Photo URL"
              value={m.photoUrl}
              onChange={(v) => updateMember(m.id, { photoUrl: v })}
            />
            <TextField
              label="Bio"
              value={m.bio ?? ""}
              multiline
              onChange={(v) => updateMember(m.id, { bio: v || undefined })}
            />

            <div
              style={{
                fontSize: 11,
                color: "var(--fg-dim)",
                marginBottom: 4,
              }}
            >
              Social links ({socials.length})
            </div>
            {socials.map((s, si) => (
              <div
                key={s.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr auto",
                  gap: 4,
                  marginBottom: 4,
                }}
              >
                <input
                  type="text"
                  value={s.platform}
                  placeholder="Platform"
                  onChange={(e) => {
                    const nx = socials.slice();
                    nx[si] = { ...s, platform: e.target.value };
                    updateMember(m.id, { socials: nx });
                  }}
                />
                <input
                  type="text"
                  value={s.url}
                  placeholder="URL"
                  onChange={(e) => {
                    const nx = socials.slice();
                    nx[si] = { ...s, url: e.target.value };
                    updateMember(m.id, { socials: nx });
                  }}
                />
                <button
                  type="button"
                  className="emke-btn is-danger"
                  onClick={() =>
                    updateMember(m.id, {
                      socials: socials.filter((x) => x.id !== s.id),
                    })
                  }
                  style={{ padding: "2px 6px" }}
                >
                  ✕
                </button>
              </div>
            ))}
            <button
              type="button"
              className="emke-btn"
              onClick={() => {
                const newSoc: TeamSocial = {
                  id: newId("ts"),
                  platform: "twitter",
                  url: "https://",
                };
                updateMember(m.id, { socials: [...socials, newSoc] });
              }}
              style={{ width: "100%", marginTop: 4 }}
            >
              + Social link
            </button>
          </div>
        );
      })}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addMember}
          style={{ width: "100%" }}
        >
          + Add member
        </button>
      </div>
    </>
  );
}
